import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
// auth
import { useAuthContext } from 'src/auth/hooks';

// Task 12.1 (§7) — the Guide system's state, fetched once per session from the
// server (never localStorage — §7's own production note says this must
// follow the user across devices). Exposes both the raw state and the
// actions every onboarding surface (welcome modal, tour engine, Help menu)
// needs, so none of them has to know the API shape.

const OnboardingContext = createContext(null);

export function OnboardingProvider({ children }) {
  const { authenticated } = useAuthContext();
  const [state, setState] = useState(null);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTourKey, setActiveTourKey] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.onboarding.state);
      setState(res.data?.data || null);
    } catch (err) {
      // Non-fatal — the Guide system is a layer on top of the app, not a
      // gate. A failed fetch just means no welcome modal/tour this session.
      console.error('Failed to load onboarding state:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }
    refresh();
    axios.get(API_ENDPOINTS.onboarding.tours)
      .then((res) => setTours(res.data?.data || []))
      .catch((err) => console.error('Failed to load tours:', err));
  }, [authenticated, refresh]);

  const completeWelcome = useCallback(async (payload) => {
    const res = await axios.post(API_ENDPOINTS.onboarding.welcome, payload);
    await refresh();
    return res.data?.data;
  }, [refresh]);

  const startTour = useCallback((tourKey) => setActiveTourKey(tourKey), []);
  const stopTour = useCallback(() => setActiveTourKey(null), []);

  const completeTour = useCallback(async (tourKey) => {
    setActiveTourKey(null);
    try {
      await axios.post(API_ENDPOINTS.onboarding.completeTour(tourKey));
    } finally {
      refresh();
    }
  }, [refresh]);

  const dismissTour = useCallback(async (tourKey) => {
    setActiveTourKey(null);
    try {
      await axios.post(API_ENDPOINTS.onboarding.dismissTour(tourKey));
    } finally {
      refresh();
    }
  }, [refresh]);

  const dismissChecklistStep = useCallback(async (stepKey) => {
    await axios.post(API_ENDPOINTS.onboarding.dismissChecklistStep(stepKey));
    refresh();
  }, [refresh]);

  const seeHotspot = useCallback(async (hotspotKey) => {
    // Optimistic: don't wait on the network to stop showing the pulse again.
    setState((prev) => (prev ? { ...prev, seenHotspotKeys: [...(prev.seenHotspotKeys || []), hotspotKey] } : prev));
    try {
      await axios.post(API_ENDPOINTS.onboarding.seeHotspot(hotspotKey));
    } catch (err) {
      console.error('Failed to record hotspot seen:', err);
    }
  }, []);

  const markWhatsNewSeen = useCallback(async () => {
    await axios.post(API_ENDPOINTS.onboarding.markWhatsNewSeen);
    refresh();
  }, [refresh]);

  const resetOnboarding = useCallback(async () => {
    await axios.post(API_ENDPOINTS.onboarding.reset);
    await refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    state,
    tours,
    loading,
    activeTourKey,
    refresh,
    completeWelcome,
    startTour,
    stopTour,
    completeTour,
    dismissTour,
    dismissChecklistStep,
    seeHotspot,
    markWhatsNewSeen,
    resetOnboarding,
  }), [state, tours, loading, activeTourKey, refresh, completeWelcome, startTour, stopTour, completeTour, dismissTour, dismissChecklistStep, seeHotspot, markWhatsNewSeen, resetOnboarding]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

OnboardingProvider.propTypes = {
  children: PropTypes.node,
};

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within an OnboardingProvider');
  return ctx;
}
